import { BrowserRouter, Navigate, Route, Routes, Outlet } from 'react-router-dom'
import { useAuthStore } from './context/auth'
import { AppLayout } from './components/layout/AppLayout'
import { LandingPage } from './pages/landing/LandingPage'
import { VerifyCertificatePage } from './pages/landing/VerifyCertificatePage'
import { VerifyDocumentPage } from './pages/landing/VerifyDocumentPage'
import { BlogListPage, BlogPostPage } from './pages/landing/BlogPage'
import { BookingPage } from './pages/agendar/BookingPage'
import { CitaPage } from './pages/agendar/CitaPage'
import { BlogAdminPage } from './pages/admin/BlogAdminPage'
import { LoginPage } from './pages/auth/LoginPage'
import { DashboardPage } from './pages/dashboard/DashboardPage'
import { AgendaPage } from './pages/agenda/AgendaPage'
import { WaitingRoomScreen } from './pages/agenda/WaitingRoomScreen'
import { RutaDelDiaPage } from './pages/agenda/RutaDelDiaPage'
import { ControlesPage } from './pages/agenda/ControlesPage'
import { EncounterPage } from './pages/clinica/EncounterPage'
import { AnamnesisTemplatesPage } from './pages/clinica/AnamnesisTemplatesPage'
import { PatientsPage } from './pages/crm/PatientsPage'
import { PatientDetailPage } from './pages/crm/PatientDetailPage'
import { PreocupacionalPage } from './pages/crm/PreocupacionalPage'
import { LeadsPage } from './pages/crm/LeadsPage'
import { CobroPage } from './pages/pagos/CobroPage'
import { ContabilidadPage } from './pages/contabilidad/ContabilidadPage'
import { HonorariosPage } from './pages/contabilidad/HonorariosPage'
import { ProduccionPage } from './pages/contabilidad/ProduccionPage'
import { MantenedoresPage } from './pages/admin/MantenedoresPage'
import { QuotesAdminPage } from './pages/admin/QuotesAdminPage'
import { PortalLayout } from './pages/empresa/PortalLayout'
import { PortalLoginPage } from './pages/empresa/PortalLoginPage'
import { ActivatePage } from './pages/empresa/ActivatePage'
import { PortalDashboardPage } from './pages/empresa/PortalDashboardPage'
import { PortalQuotesPage } from './pages/empresa/PortalQuotesPage'
import { PortalWorkersPage } from './pages/empresa/PortalWorkersPage'
import { PortalBillingPage } from './pages/empresa/PortalBillingPage'
import { PatientLoginPage } from './pages/paciente/PatientLoginPage'
import { PatientPortalLayout } from './pages/paciente/PatientPortalLayout'
import { MisCitasPage } from './pages/paciente/MisCitasPage'
import { MisExamenesPage } from './pages/paciente/MisExamenesPage'
import { MisRecetasPage } from './pages/paciente/MisRecetasPage'
import { MisControlesPage } from './pages/paciente/MisControlesPage'
import { MisPagosPage } from './pages/paciente/MisPagosPage'

function RequireAuth() {
  const user = useAuthStore((s) => s.user)
  if (!user) return <Navigate to="/login" replace />
  // Los contactos de empresa y pacientes no usan el panel interno.
  if (user.role === 'COMPANY') return <Navigate to="/empresa" replace />
  if (user.role === 'PATIENT') return <Navigate to="/paciente" replace />
  return <Outlet />
}

function RequirePatient() {
  const user = useAuthStore((s) => s.user)
  if (!user) return <Navigate to="/paciente/login" replace />
  if (user.role !== 'PATIENT') return <Navigate to="/app" replace />
  return <Outlet />
}

function RequireCompany() {
  const user = useAuthStore((s) => s.user)
  if (!user) return <Navigate to="/empresa/login" replace />
  if (user.role !== 'COMPANY') return <Navigate to="/app" replace />
  return <Outlet />
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Web pública de captación */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/verificar" element={<VerifyCertificatePage />} />
        <Route path="/verificar-documento" element={<VerifyDocumentPage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        {/* Agendamiento en línea: horas primero, identidad al final (DT-56). */}
        <Route path="/agendar" element={<BookingPage />} />
        {/* Autoservicio de la cita por enlace del correo, sin login (DT-57). */}
        <Route path="/cita/:token" element={<CitaPage />} />
        <Route path="/login" element={<LoginPage />} />
        {/* Monitor de la sala de espera: kiosco sin sesión (ver DT-32). */}
        <Route path="/llamados" element={<WaitingRoomScreen />} />

        {/* Portal de empresa (B2B) */}
        <Route path="/empresa/login" element={<PortalLoginPage />} />
        <Route path="/empresa/activar/:token" element={<ActivatePage />} />
        <Route path="/empresa" element={<RequireCompany />}>
          <Route element={<PortalLayout />}>
            <Route index element={<PortalDashboardPage />} />
            <Route path="cotizaciones" element={<PortalQuotesPage />} />
            <Route path="trabajadores" element={<PortalWorkersPage />} />
            <Route path="facturacion" element={<PortalBillingPage />} />
            <Route path="*" element={<Navigate to="/empresa" replace />} />
          </Route>
        </Route>

        {/* Portal de paciente */}
        <Route path="/paciente/login" element={<PatientLoginPage />} />
        <Route path="/paciente/activar/:token" element={<ActivatePage portal="paciente" />} />
        <Route path="/paciente" element={<RequirePatient />}>
          <Route element={<PatientPortalLayout />}>
            <Route index element={<MisCitasPage />} />
            <Route path="examenes" element={<MisExamenesPage />} />
            <Route path="recetas" element={<MisRecetasPage />} />
            <Route path="controles" element={<MisControlesPage />} />
            <Route path="pagos" element={<MisPagosPage />} />
            <Route path="*" element={<Navigate to="/paciente" replace />} />
          </Route>
        </Route>

        {/* Panel interno */}
        <Route path="/app" element={<RequireAuth />}>
          <Route element={<AppLayout />}>
            <Route index element={<DashboardPage />} />
            <Route path="agenda" element={<AgendaPage />} />
            <Route path="ruta" element={<RutaDelDiaPage />} />
            <Route path="controles" element={<ControlesPage />} />
            <Route path="pacientes" element={<PatientsPage />} />
            <Route path="pacientes/:id" element={<PatientDetailPage />} />
            <Route path="atencion/:id" element={<EncounterPage />} />
            <Route path="plantillas" element={<AnamnesisTemplatesPage />} />
            <Route path="preocupacional" element={<PreocupacionalPage />} />
            <Route path="cotizaciones" element={<QuotesAdminPage />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="cobros" element={<CobroPage />} />
            <Route path="contabilidad" element={<ContabilidadPage />} />
            <Route path="honorarios" element={<HonorariosPage />} />
            <Route path="produccion" element={<ProduccionPage />} />
            <Route path="mantenedores" element={<MantenedoresPage />} />
            <Route path="blog" element={<BlogAdminPage />} />
            <Route path="*" element={<Navigate to="/app" replace />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
